import i18next from "i18next";

import { growthbook } from "./growthbook";
import { FEATURE_FLAGS } from "../hooks/useFeatureFlag";

export const SUPPORTED_LANGUAGES = ["en", "de", "it", "fr"] as const;

export const DEFAULT_LANGUAGE = "en";

/**
 * Normalize an arbitrary language string ("de-DE", "FR", etc.) to a supported
 * language code, or null if it isn't one we ship.
 */
export const normalizeLanguage = (language: string | null | undefined): string | null => {
  const languageCode = (language || "").toLowerCase().split("-")[0];
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(languageCode) ? languageCode : null;
};

/**
 * Whether localization is currently enabled for the active GrowthBook context.
 *
 * The "localization" flag keys off the crew/user attributes, so callers must
 * ensure GrowthBook attributes have been set for the active crew before relying
 * on this (see setGrowthBookAttributes in pages/index.js).
 */
export const isLocalizationEnabled = (): boolean => growthbook.isOn(FEATURE_FLAGS.LOCALIZATION);

/**
 * Resolve the language that should be active right now.
 *
 * Localization is gated behind the "localization" feature flag for internal
 * testing. While the flag is off we always fall back to English, regardless of
 * the user's preferred/profile language, so non-flagged sessions look exactly
 * as they did before localization was introduced. The user's profile setting is
 * the single source of truth otherwise — there is no cookie/localStorage/browser
 * detection.
 */
export const resolveActiveLanguage = (preferredLanguage: string | null | undefined): string => {
  if (!isLocalizationEnabled()) {
    return DEFAULT_LANGUAGE;
  }
  return normalizeLanguage(preferredLanguage) ?? DEFAULT_LANGUAGE;
};

/**
 * Apply the correct language for the current flag state. Call this after the
 * active crew (and therefore GrowthBook attributes) changes, or after the user
 * updates their preferred language. No-op if the resolved language is already
 * active.
 */
export const applyActiveLanguage = (preferredLanguage: string | null | undefined): void => {
  // i18next is initialized in src/index.tsx; bail out if it isn't ready yet
  // (also keeps this safe to call from tests that don't bootstrap i18next).
  if (!i18next.isInitialized) {
    return;
  }
  const language = resolveActiveLanguage(preferredLanguage);
  if (i18next.language !== language) {
    i18next.changeLanguage(language);
  }
};
